import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useView } from '../contexts/ViewContext';
import { Card, CardHeader } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ShoppingCart, Package, FileText, Loader2, CalendarRange } from 'lucide-react';
import { purchasingAPI, coreAPI, platformSettingsAPI, PurchaseOrder, CommodityGroup, CommodityType, Site, CompanyGroup } from '../lib/api';
import type { PurchasingTabsVisibility } from '../lib/api/types/common';
import {
  DEFAULT_PURCHASING_TABS,
  normalizePurchasingTabs,
  RETIRED_PURCHASING_URL_TABS,
} from '../components/admin/settings/purchasingTabsConfig';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { getPortalLabels } from '../lib/portalLabels';
import { usePortalMode } from '../hooks/usePortalMode';
import { Cache } from '../lib/cache';
import PODetailModal from '../components/purchasing/PODetailModal';
import POHeadersTab from '../components/purchasing/tabs/POHeadersTab';
import POLinesTab from '../components/purchasing/tabs/POLinesTab';
import ASNTab from '../components/purchasing/tabs/ASNTab';
import POLineSchedules from './POLineSchedules';
import { usePagination, useModal } from '../hooks';
import { getErrorMessage } from '../hooks/useFetchData';

export default function Purchasing() {
  const { isFeatureEnabled } = useMenuSettings();
  const { portalMode, isCustomer } = usePortalMode();
  const { viewKey, getViewQueryParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const [searchParams, setSearchParams] = useSearchParams();
  const portalLabels = getPortalLabels(portalMode);

  // Read URL parameters for deep linking
  const urlTab = searchParams.get('tab');
  const urlPo = searchParams.get('po');
  const urlLine = searchParams.get('line');
  const urlSearch = searchParams.get('search');
  const urlLineTab = searchParams.get('lineTab');  // CR-3929: Auto-open specific tab in line detail

  const showSchedules = isFeatureEnabled('po_line_schedules');

  // Purchasing tab visibility from platform settings
  const [tabVisibility, setTabVisibility] = useState<PurchasingTabsVisibility>(DEFAULT_PURCHASING_TABS);
  const [tabsReady, setTabsReady] = useState(false);

  useEffect(() => {
    platformSettingsAPI.getDisplaySettings()
      .then(res => {
        if (res.data?.purchasing_tabs) {
          setTabVisibility(normalizePurchasingTabs(res.data.purchasing_tabs));
        }
      })
      .catch(() => { /* use defaults */ })
      .finally(() => setTabsReady(true));
  }, []);

  // Default tab:
  //  - when schedule_collaboration is enabled, land on Schedules
  //  - otherwise fall back to PO Lines
  //  - explicit ?tab=... in the URL always wins (removed tabs redirect to po_lines)
  const scheduleCollabEnabled = isFeatureEnabled('schedule_collaboration');
  const getValidTab = (tab: string | null): string => {
    if (!tab) return scheduleCollabEnabled ? 'schedules' : 'po_lines';
    if (RETIRED_PURCHASING_URL_TABS.has(tab)) return 'po_lines';
    if (tab === 'schedules' && !showSchedules) return 'po_lines';
    return tab;
  };

  const [mainTab, setMainTabState] = useState(getValidTab(urlTab));

  // Sync main tab when URL params change (e.g. notification click while already on page)
  useEffect(() => {
    const newTab = searchParams.get('tab');
    if (newTab && newTab !== mainTab) {
      setMainTabState(getValidTab(newTab));
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wrapper to enforce tab restrictions
  const setMainTab = (tab: string) => {
    setMainTabState(getValidTab(tab));
  };
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const [openOrderValue, setOpenOrderValue] = useState<number>(0);
  const [showFilters, setShowFilters] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [poSortField, setPOSortField] = useState('-issue_date');
  const pagination = usePagination({ pageSize: 50 });
  const detailModal = useModal<number>();

  // Text input states (for debouncing) - CR-3954: Initialize from URL search param
  const [textInputs, setTextInputs] = useState({
    poNumber: urlSearch || '',
    customer: '',
    supplier: '',
  });

  // CR-4580: Reset PO Header filters on unmount so they don't persist
  useEffect(() => {
    return () => {
      setFilters({ poNumber: '', customer: '', supplier: '', status: '', dateFrom: '', dateTo: '', site: '', customerGroup: '', commodityGroup: '', commodityType: '', pdfAttached: '', strategic: '' });
      setTextInputs({ poNumber: '', customer: '', supplier: '' });
    };
  }, []);

  // Actual filter state (used for API calls)
  const [filters, setFilters] = useState({
    poNumber: urlSearch || '',
    customer: '',
    supplier: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    site: '',
    customerGroup: '',
    commodityGroup: '',
    commodityType: '',
    pdfAttached: '',
    strategic: '',
  });

  // Filter dropdown options
  const [siteOptions, setSiteOptions] = useState<{ value: string; label: string }[]>([]);
  const [customerGroupOptions, setCustomerGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityGroupOptions, setCommodityGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityTypeOptions, setCommodityTypeOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    setInitialLoadComplete(true);

    // Check cache for filter options (stable reference data, cached 15 min)
    const FILTER_OPTS_CACHE_KEY = 'purchasing_header_filter_options';
    const cached = Cache.get<{
      sites: { value: string; label: string }[];
      commodityGroups: { value: string; label: string }[];
      commodityTypes: { value: string; label: string }[];
      customerGroups: { value: string; label: string }[];
    }>(FILTER_OPTS_CACHE_KEY);

    if (cached) {
      setSiteOptions(cached.sites);
      setCommodityGroupOptions(cached.commodityGroups);
      setCommodityTypeOptions(cached.commodityTypes);
      setCustomerGroupOptions(cached.customerGroups);
      return;
    }

    // Fetch filter options for PO Headers
    Promise.all([
      coreAPI.getAccessibleSites().catch(() => ({ data: [] })),
      purchasingAPI.getCommodityGroups({ enabled: true }).catch(() => ({ data: [] })),
      purchasingAPI.getCommodityTypes({ enabled: true }).catch(() => ({ data: [] })),
      coreAPI.getCustomerGroupsForFilter().catch(() => ({ data: { results: [] } })),
    ]).then(([sitesRes, groupsRes, typesRes, companyGroupsRes]) => {
      const sites = (Array.isArray(sitesRes.data) ? sitesRes.data : (sitesRes.data as unknown as { results: Site[] }).results || []) as Site[];
      const siteOpts = sites.map((s) => ({ value: String(s.id), label: s.name }));
      setSiteOptions(siteOpts);

      const groups = Array.isArray(groupsRes.data) ? groupsRes.data : (groupsRes.data as unknown as { results: CommodityGroup[] }).results || [];
      const groupOpts = groups.filter((g: CommodityGroup) => g.enabled).map((g: CommodityGroup) => ({ value: String(g.id), label: g.name }));
      setCommodityGroupOptions(groupOpts);

      const types = Array.isArray(typesRes.data) ? typesRes.data : (typesRes.data as unknown as { results: CommodityType[] }).results || [];
      const typeOpts = types.filter((t: CommodityType) => t.enabled).map((t: CommodityType) => ({ value: String(t.id), label: t.name }));
      setCommodityTypeOptions(typeOpts);

      const companyGroups = (companyGroupsRes.data as unknown as { results: CompanyGroup[] }).results || [];
      const customerGroupOpts = companyGroups.map((g: CompanyGroup) => ({ value: String(g.id), label: g.name }));
      setCustomerGroupOptions(customerGroupOpts);

      // Cache for 15 minutes (reference data changes rarely)
      Cache.set(FILTER_OPTS_CACHE_KEY, {
        sites: siteOpts,
        commodityGroups: groupOpts,
        commodityTypes: typeOpts,
        customerGroups: customerGroupOpts,
      }, 15 * 60 * 1000);
    });
  }, []);

  // CR-3929/CR-3968: Auto-open PO detail or PO line from notification
  useEffect(() => {
    if (urlLine) {
      // Deep link to specific PO line - switch to po_lines tab
      setMainTab('po_lines');
    } else if (urlPo) {
      // Look up PO by number to get the database ID
      purchasingAPI.getOrders({ po_number: urlPo, page_size: 1 }).then((res) => {
        if (res.data.results.length > 0) {
          detailModal.open(res.data.results[0].id);
        }
      }).catch(() => {
        // Silently ignore lookup failures
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPo, urlLine]);

  // Text inputs are applied on explicit Search button click only
  // (no auto-debounce — avoids jittery re-renders while typing)

  useEffect(() => {
    if (viewContextLoading) return;
    // Only fetch PO headers when the PO Headers tab is active.
    // Other tabs (PO Lines, RFQ, etc.) manage their own data fetching.
    if (initialLoadComplete && mainTab === 'po_headers') {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewContextLoading, pagination.currentPage, filters, activeTab, initialLoadComplete, mainTab, portalMode, viewKey, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, poSortField]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters based on filters and active tab
      const queryParams: Record<string, string | number | boolean> = {
        ordering: poSortField,
        page: pagination.currentPage,
        page_size: pagination.pageSize,
      };

      // Create a cache key. MUST include the view context (site / group /
      // company) — otherwise switching the top-bar site selector returns a
      // stale response cached for a different scope.
      const viewParamsForKey = getViewQueryParams();
      const cacheKey = `purchasing_${activeTab}_${pagination.currentPage}_${poSortField}_${JSON.stringify(filters)}_${JSON.stringify(viewParamsForKey)}`;

      // Check cache first. Tab counts + open-order-value MUST be in the
      // cached object — otherwise a hit returns the orders correctly but
      // leaves the badge counts stuck at the previous scope's values.
      const cachedData = Cache.get<{
        orders: PurchaseOrder[];
        totalCount: number;
        tabCounts?: { all: number; open: number; closed: number };
        openOrderValue?: number;
      }>(cacheKey);
      if (cachedData) {
        setOrders(cachedData.orders);
        pagination.setFromResponse(cachedData.totalCount);
        if (cachedData.tabCounts) setTabCounts(cachedData.tabCounts);
        if (cachedData.openOrderValue !== undefined) setOpenOrderValue(cachedData.openOrderValue);
        setLoading(false);
        return;
      }

      // Individual field filters using Django filter syntax
      if (filters.poNumber) {
        queryParams['po_number__icontains'] = filters.poNumber;
      }

      if (filters.customer) {
        // Normalize special chars for fuzzy matching (P.P.U → P P U)
        queryParams['customer__name__icontains'] = filters.customer.replace(/[.\-_+]/g, ' ').replace(/\s+/g, ' ').trim();
      }

      if (filters.supplier) {
        queryParams['supplier__name__icontains'] = filters.supplier;
      }

      // Apply status filter from dropdown or active tab
      if (filters.status) {
        queryParams['status'] = filters.status;
      } else if (activeTab !== 'all') {
        // Tab-based filtering
        if (activeTab === 'open') {
          queryParams['status__in'] = 'sent,acknowledged,in_progress';
        } else if (activeTab === 'delivered') {
          queryParams['status'] = 'delivered';
        } else if (activeTab === 'closed') {
          queryParams['status'] = 'closed';
        }
        // Note: 'on_hold' tab would need backend support for filtering by line.on_hold
      }

      // Apply date filters
      if (filters.dateFrom) {
        queryParams['issue_date__gte'] = filters.dateFrom;
      }
      if (filters.dateTo) {
        queryParams['issue_date__lte'] = filters.dateTo;
      }

      // New PO Header filters
      if (filters.site) {
        queryParams['supplier'] = filters.site;
      }
      if (filters.customerGroup) {
        queryParams['customer__group'] = filters.customerGroup;
      }
      if (filters.commodityGroup) {
        queryParams['lines__commodity_group'] = filters.commodityGroup;
      }
      if (filters.commodityType) {
        queryParams['lines__commodity_type'] = filters.commodityType;
      }

      // Spread view context params (group/company/site) for multi-supplier support
      const viewParams = getViewQueryParams();
      Object.entries(viewParams).forEach(([key, value]) => {
        if (value !== undefined) queryParams[key] = value;
      });

      // Fetch orders and tab counts in parallel
      const countParams = { ...viewParams, page_size: 1 };
      const [ordersResponse, allRes, openRes, closedRes, statsRes] = await Promise.all([
        purchasingAPI.getOrders(queryParams),
        purchasingAPI.getOrders({ ...countParams, page_size: 1 }),
        purchasingAPI.getOrders({ ...countParams, status__in: 'sent,acknowledged,in_progress', page_size: 1 }),
        purchasingAPI.getOrders({ ...countParams, status: 'closed', page_size: 1 }),
        purchasingAPI.getDashboardStats(),
      ]);

      setOrders(ordersResponse.data.results);
      pagination.setFromResponse(ordersResponse.data.count);
      setTabCounts({
        all: allRes.data.count,
        open: openRes.data.count,
        closed: closedRes.data.count,
      });
      setOpenOrderValue(statsRes.data.open_order_value || 0);

      // Cache the data for 2 minutes (since purchasing data changes more frequently than dashboard)
      Cache.set(cacheKey, {
        orders: ordersResponse.data.results,
        totalCount: ordersResponse.data.count,
        tabCounts: {
          all: allRes.data.count,
          open: openRes.data.count,
          closed: closedRes.data.count,
        },
        openOrderValue: statsRes.data.open_order_value || 0,
      }, 2 * 60 * 1000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load sales orders data'));
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setTextInputs({
      poNumber: '',
      customer: '',
      supplier: '',
    });
    setFilters({
      poNumber: '',
      customer: '',
      supplier: '',
      status: '',
      dateFrom: '',
      dateTo: '',
      site: '',
      customerGroup: '',
      commodityGroup: '',
      commodityType: '',
      pdfAttached: '',
      strategic: '',
    });
    pagination.reset();
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      pagination.setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSearch = () => {
    const newFilters = {
      ...filters,
      poNumber: textInputs.poNumber,
      customer: textInputs.customer,
      supplier: textInputs.supplier,
    };
    setFilters(newFilters);
    pagination.reset();
    // Invalidate cache and fetch immediately with new filters. Key must
    // match the one built in fetchData(), which now includes view context.
    const viewParamsForKey = getViewQueryParams();
    const cacheKey = `purchasing_${activeTab}_1_${poSortField}_${JSON.stringify(newFilters)}_${JSON.stringify(viewParamsForKey)}`;
    Cache.remove(cacheKey);
  };

  const handleFilterChange = (key: string, value: string) => {
    // Text inputs: update textInputs state (will be debounced)
    if (['poNumber', 'customer', 'supplier'].includes(key)) {
      setTextInputs({ ...textInputs, [key]: value });
    } else if (key === 'commodityGroup') {
      // Cascade: reset commodity type when group changes
      setFilters({ ...filters, commodityGroup: value, commodityType: '' });
    } else {
      // Dropdowns and dates: update filters directly (instant)
      setFilters({ ...filters, [key]: value });
    }
    pagination.reset(); // Reset to first page when filtering
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    pagination.reset(); // Reset to first page when changing tabs
  };

  const handlePONumberClick = (poNumber: string) => {
    setSearchParams({ tab: 'po_lines', poFilter: poNumber }, { replace: true });
    setMainTab('po_lines');
  };

  // Render a loading placeholder for the PO Headers content area (shown inside the tab layout,
  // NOT as a full-page replacement, so the tab bar stays visible and doesn't flicker).
  const renderPOHeadersContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="mt-2 text-sm text-muted-foreground">Loading sales orders...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button
              onClick={fetchData}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return (
      <POHeadersTab
        orders={orders}
        loading={loading}
        totalCount={pagination.totalCount}
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
        onRowClick={(poId) => detailModal.open(poId)}
        onPONumberClick={handlePONumberClick}
        textInputs={textInputs}
        filters={filters}
        siteOptions={siteOptions}
        customerGroupOptions={customerGroupOptions}
        commodityGroupOptions={commodityGroupOptions}
        commodityTypeOptions={commodityTypeOptions}
        onFilterChange={handleFilterChange}
        onClearFilters={clearFilters}
        showFilters={showFilters}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onRefresh={fetchData}
        onSearch={handleSearch}
        onToggleFilters={() => setShowFilters(!showFilters)}
        tabCounts={tabCounts}
        openOrderValue={openOrderValue}
        sortField={poSortField}
        sortOrder={poSortField.startsWith('-') ? 'desc' : 'asc'}
        onSort={(field) => {
          setPOSortField(prev => {
            if (prev === field) return `-${field}`;
            if (prev === `-${field}`) return field;
            return `-${field}`;
          });
          pagination.goToPage(1);
        }}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{portalLabels.ordersPageTitle}</h1>
          <p className="text-muted-foreground mt-2">
            {isCustomer
              ? 'Manage purchase orders and track customer deliveries.'
              : 'Manage sales orders and track procurement activities.'}
          </p>
        </div>
        {/* Filters button moved inside POHeadersTab for consistency with PO Lines */}
      </div>

      {/* KPI Cards - Hidden for now */}
      {/* <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total POs</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_pos || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.total_lines || 0} lines total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Spend</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              £{((stats?.total_spend || 0) / 1000000).toFixed(1)}M
            </div>
            <p className="text-xs text-muted-foreground">
              Total procurement value
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Orders</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              £{((stats?.open_order_value || 0) / 1000000).toFixed(1)}M
            </div>
            <p className="text-xs text-muted-foreground">
              Pending/In progress
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Arrears</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              £{((stats?.arrears_value || 0) / 1000000).toFixed(1)}M
            </div>
            <p className="text-xs text-muted-foreground">
              Overdue orders
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">PPM</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${(stats?.ppm || 0) > 1500 ? 'text-red-600' : 'text-green-600'}`}>
              {(stats?.ppm || 0).toFixed(0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Target: 1,500 PPM
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">OTIF</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${(stats?.otif || 0) >= 85 ? 'text-green-600' : 'text-yellow-600'}`}>
              {stats?.otif ? `${stats.otif.toFixed(1)}%` : '0%'}
            </div>
            <p className="text-xs text-muted-foreground">
              Target: 85%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">OTD</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${(stats?.otd || 0) >= 90 ? 'text-green-600' : 'text-yellow-600'}`}>
              {stats?.otd ? `${stats.otd.toFixed(1)}%` : '0%'}
            </div>
            <p className="text-xs text-muted-foreground">
              Target: 90%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">NCRs</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.ncrs || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Non-conformance reports
            </p>
          </CardContent>
        </Card>
      </div> */}

      {/* Main Navigation Tabs */}
      {/* RFQ and Forecast tabs are supplier-only (suppliers create RFQs and share forecasts with customers) */}
      <Card className="border-2 shadow-sm">
        <CardHeader className="pb-3">
          {!tabsReady ? (
            /* Skeleton placeholder while tab visibility settings load — prevents tab bar flicker */
            <div className="flex gap-2 animate-pulse">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex-1 h-14 bg-slate-100 rounded-md" />
              ))}
            </div>
          ) : (
          <Tabs value={mainTab} onValueChange={setMainTab}>
            <TabsList className={`grid w-full h-auto bg-gradient-to-r from-slate-50 to-slate-100`} style={{ gridTemplateColumns: `repeat(${[
              tabVisibility.po_headers,
              tabVisibility.po_lines,
              showSchedules,
              tabVisibility.asn,
            ].filter(Boolean).length}, minmax(0, 1fr))` }}>
              {tabVisibility.po_headers && (
                <TabsTrigger value="po_headers" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <ShoppingCart className={`h-5 w-5 ${mainTab === 'po_headers' ? 'text-blue-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'po_headers' ? 'text-blue-700' : 'text-slate-600'}`}>SO Headers</span>
                  </div>
                </TabsTrigger>
              )}
              {tabVisibility.po_lines && (
                <TabsTrigger value="po_lines" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <Package className={`h-5 w-5 ${mainTab === 'po_lines' ? 'text-purple-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'po_lines' ? 'text-purple-700' : 'text-slate-600'}`}>SO Lines</span>
                  </div>
                </TabsTrigger>
              )}
              {showSchedules && (
                <TabsTrigger value="schedules" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <CalendarRange className={`h-5 w-5 ${mainTab === 'schedules' ? 'text-cyan-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'schedules' ? 'text-cyan-700' : 'text-slate-600'}`}>Schedules</span>
                  </div>
                </TabsTrigger>
              )}
              {tabVisibility.asn && (
                <TabsTrigger value="asn" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <FileText className={`h-5 w-5 ${mainTab === 'asn' ? 'text-amber-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'asn' ? 'text-amber-700' : 'text-slate-600'}`}>ASN</span>
                  </div>
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>
          )}
        </CardHeader>
      </Card>

      {/* Tab Content */}
      {mainTab === 'po_headers' && renderPOHeadersContent()}

      {mainTab === 'po_lines' && <POLinesTab initialLineId={urlLine ? parseInt(urlLine, 10) : null} initialLineTab={urlLineTab || undefined} />}

      {mainTab === 'schedules' && showSchedules && <POLineSchedules />}

      {mainTab === 'asn' && <ASNTab />}

      {/* PO Detail Modal */}
      <PODetailModal
        isOpen={detailModal.isOpen}
        onClose={detailModal.close}
        poId={detailModal.data || 0}
      />
    </div>
  );
}
